import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useGroupChannel } from '@sendbird/uikit-react/GroupChannel/context';
import ChannelAvatar from '@sendbird/uikit-react/ui/ChannelAvatar';
import { generatePath } from 'react-router-dom';
import ROUTES_PATH from 'domain/router/routesPathConfig';
import { ChannelContext } from '../../types';

import { userProfileSelector } from 'domain/env';
import { chatParticipantsSelector } from 'domain/chat';
import { companiesByIdSelector } from 'domain/companies';
import { getChannelTitle, resolveChannelContext } from '../../helpers';
import ChatDocumentPreviewModal from '../ChatDocumentPreviewModal';
import ChatHeaderView from './ChatHeaderView';

interface Props {
  onToggleSettings: (v: boolean) => void;
  onClose: () => void;
  onBackToList?: () => void;
  /** Mobile view — disables new-tab navigations in the header (see ChatHeaderView). */
  isMobile?: boolean;
}

const ChatHeader: React.FC<Props> = ({ onToggleSettings, onClose, onBackToList, isMobile = false }) => {
  const { formatMessage } = useIntl();
  const { state } = useGroupChannel();
  const channel = state?.currentChannel;
  const userProfile = useSelector(userProfileSelector);
  const chatParticipants = useSelector(chatParticipantsSelector);
  const companies = useSelector(companiesByIdSelector);
  const [showPreview, setShowPreview] = useState(false);

  if (!channel) return null;

  const ctx: ChannelContext | null = resolveChannelContext(channel);
  const isAP = ctx?.app === 'AP';
  const hasDocumentPreview = Boolean(ctx?.documentId && ctx?.companyId);
  const isMultiCompany = companies.size > 1;
  const companyName =
    isMultiCompany && ctx?.companyId ? (companies.get(ctx.companyId)?.cname as string | undefined) || null : null;
  const companyHref =
    isAP && ctx?.companyId
      ? `${generatePath(ROUTES_PATH.COMPANY_WORKSPACE_CATEGORY_UNDEFINED.absolute, { companyId: ctx.companyId })}?chatChannel=${encodeURIComponent(channel.url)}`
      : null;

  const contextPath =
    isAP && ctx?.companyId
      ? ctx.documentId
        ? generatePath(ROUTES_PATH.DOCUMENT.absolute, { companyId: ctx.companyId, documentId: ctx.documentId })
        : generatePath(ROUTES_PATH.COMPANY_WORKSPACE_CATEGORY_UNDEFINED.absolute, { companyId: ctx.companyId })
      : null;

  const showOpenInNewTab = Boolean(contextPath);

  const channelName = getChannelTitle(
    channel,
    userProfile.id,
    chatParticipants,
    formatMessage({ id: 'chat.channelList.unnamedChat', defaultMessage: 'Group name' }),
  );

  return (
    <>
      <ChatHeaderView
        channelName={channelName}
        avatar={<ChannelAvatar channel={channel} width={32} height={32} userId={userProfile.id} theme="light" />}
        companyName={companyName}
        companyHref={companyHref}
        onPreviewDocument={hasDocumentPreview ? () => setShowPreview(true) : null}
        openInNewTabHref={showOpenInNewTab ? contextPath : null}
        isMobile={isMobile}
        onToggleSettings={() => onToggleSettings(true)}
        showSettings={!channel.isFrozen}
        onClose={onClose}
        onBackToList={onBackToList}
      />
      {showPreview && hasDocumentPreview && (
        <ChatDocumentPreviewModal
          companyId={String(ctx!.companyId)}
          documentId={String(ctx!.documentId)}
          onClose={() => setShowPreview(false)}
          isMobile={isMobile}
        />
      )}
    </>
  );
};

export default ChatHeader;
